import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { Container } from '../components/layout/Primitives';
import JournalPagination from '../components/journal/JournalPagination';
import PageSeo from '../components/seo/PageSeo';
import { Button } from '../components/ui/Button';
import { isApiEnabled } from '../config/env';
import { useLanguage } from '../contexts/LanguageContext';
import { getBlogTags, getPosts, normalizePostsList, normalizeTags } from '../services/blogService';

const PAGE_SIZE = 12;
const p = 'modamai.blog';

const PageShell = styled.div`
  padding: clamp(24px, 3vw, 40px) 0 clamp(40px, 5vw, 64px);
`;

const Breadcrumb = styled.nav`
  margin-bottom: 20px;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};

  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: ${({ theme }) => theme.colors.foreground};
    }
  }

  span[aria-hidden] {
    margin: 0 0.5rem;
  }
`;

const PageHeader = styled.header`
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Title = styled.h1`
  margin: 0 0 10px;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.75rem, 2vw + 1rem, 2.25rem);
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Lead = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  max-width: 32rem;
  line-height: 1.6;
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 16px 20px;
  }
`;

const SearchForm = styled.form`
  display: flex;
  gap: 8px;
  flex: 1;
  min-width: min(100%, 18rem);
  max-width: min(100%, 22rem);
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 9px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  font-size: ${({ theme }) => theme.type.bodySm};
  font-family: inherit;
`;

const SearchBtn = styled.button`
  padding: 9px 14px;
  border: 1px solid ${({ theme }) => theme.colors.foreground};
  background: ${({ theme }) => theme.colors.foreground};
  color: ${({ theme }) => theme.colors.background};
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: inherit;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: min(100%, 12rem);
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Select = styled.select`
  width: 100%;
  min-width: 12rem;
  padding: 9px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  font-size: ${({ theme }) => theme.type.bodySm};
  font-family: inherit;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
`;

const FiltersBlock = styled.div`
  margin-bottom: clamp(28px, 3.5vw, 40px);
`;

const ActiveFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`;

const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.foreground};
`;

const ChipBtn = styled.button`
  display: inline-flex;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const ClearLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-family: inherit;
`;

const GridWrap = styled.div`
  position: relative;
  margin-bottom: clamp(32px, 4vw, 48px);
  opacity: ${({ $busy }) => ($busy ? 0.55 : 1)};
  transition: opacity 0.2s ease;
  pointer-events: ${({ $busy }) => ($busy ? 'none' : 'auto')};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 14px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 22px 20px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
`;

const Thumb = styled.div`
  aspect-ratio: 4 / 3;
  margin-bottom: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.secondary};
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.45s ease;

  ${Card}:hover & {
    transform: scale(1.04);
  }
`;

const ThumbPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    145deg,
    ${({ theme }) => theme.colors.secondary} 0%,
    ${({ theme }) => theme.colors.border} 100%
  );
`;

const Meta = styled.p`
  margin: 0 0 6px;
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const CardTitle = styled.h2`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(0.9rem, 0.35vw + 0.8rem, 1.05rem);
  font-weight: 400;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Excerpt = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Empty = styled.div`
  text-align: center;
  padding: 48px 24px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.type.bodySm};
`;

const ApiOff = styled.div`
  text-align: center;
  padding: 48px 24px;
  max-width: 28rem;
  margin: 0 auto;
  font-size: ${({ theme }) => theme.type.body};
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.6;
`;

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

function parsePage(raw) {
  const n = parseInt(raw || '1', 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export default function Blog() {
  const { t } = useTranslation();
  const { locale } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const useApi = isApiEnabled();

  const page = parsePage(searchParams.get('page'));
  const activeTag = searchParams.get('tag') || '';
  const searchQ = searchParams.get('q') || '';
  const sort = searchParams.get('sort') === 'oldest' ? 'oldest' : 'newest';

  const [searchInput, setSearchInput] = useState(searchQ);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, pages: 1 });
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setSearchInput(searchQ);
  }, [searchQ]);

  const pushQuery = useCallback(
    (updates, { scrollTop = false } = {}) => {
      const params = new URLSearchParams(searchParams.toString());
      Object.entries(updates).forEach(([key, value]) => {
        if (
          value === '' ||
          value === null ||
          value === undefined ||
          (key === 'page' && value === 1) ||
          (key === 'sort' && value === 'newest')
        ) {
          params.delete(key);
        } else {
          params.set(key, String(value));
        }
      });
      setSearchParams(params, { replace: false });
      if (scrollTop && typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [searchParams, setSearchParams]
  );

  const load = useCallback(() => {
    if (!useApi) return;
    setLoading(true);
    setError('');
    getPosts(
      {
        page,
        limit: PAGE_SIZE,
        sort,
        ...(activeTag ? { tag: activeTag } : {}),
        ...(searchQ.trim() ? { q: searchQ.trim() } : {}),
      },
      locale
    )
      .then((res) => {
        setPosts(normalizePostsList(res));
        setPagination(res?.pagination || { page, limit: PAGE_SIZE, total: 0, pages: 1 });
      })
      .catch((e) => {
        setError(e?.response?.data?.message || e.message || t(`${p}.loadPostsFailed`));
      })
      .finally(() => setLoading(false));
  }, [useApi, page, activeTag, searchQ, sort, locale, t]);

  useEffect(() => {
    if (!useApi) return undefined;
    load();
    return undefined;
  }, [load, useApi]);

  useEffect(() => {
    if (!useApi) return undefined;
    setTagsLoading(true);
    getBlogTags(locale)
      .then((res) => setTags(normalizeTags(res)))
      .catch(() => setTags([]))
      .finally(() => setTagsLoading(false));
    return undefined;
  }, [useApi, locale]);

  const handleSearch = (e) => {
    e.preventDefault();
    pushQuery({ q: searchInput.trim(), page: 1 }, { scrollTop: true });
  };

  const handleTagChange = (e) => {
    pushQuery({ tag: e.target.value, page: 1 }, { scrollTop: true });
  };

  const handleSortChange = (e) => {
    pushQuery({ sort: e.target.value, page: 1 }, { scrollTop: true });
  };

  const clearFilters = () => {
    pushQuery({ tag: '', q: '', page: 1, sort: 'newest' }, { scrollTop: true });
    setSearchInput('');
  };

  const totalPages = pagination?.pages ?? 1;
  const currentPage = pagination?.page ?? page;
  const total = pagination?.total ?? 0;
  const limit = pagination?.limit ?? PAGE_SIZE;
  const hasActiveFilters = Boolean(activeTag || searchQ.trim() || sort === 'oldest');

  if (!useApi) {
    return (
      <Container>
        <Title>{t(`${p}.title`)}</Title>
        <ApiOff>
          {t(`${p}.apiOffBefore`)} <code>REACT_APP_API_URL</code> {t(`${p}.apiOffAfter`)}
        </ApiOff>
      </Container>
    );
  }

  return (
    <Container>
      <PageSeo
        documentTitle={t(`${p}.title`)}
        description={t(`${p}.lead`)}
        canonicalPath="/blog"
      />
      <PageShell>
        <Breadcrumb aria-label={t('modamai.shop.breadcrumbAria')}>
          <Link to="/">{t('modamai.shop.home')}</Link>
          <span aria-hidden>/</span>
          <span>{t(`${p}.title`)}</span>
        </Breadcrumb>

        <PageHeader>
          <Title>{t(`${p}.title`)}</Title>
          <Lead>{t(`${p}.lead`)}</Lead>
        </PageHeader>

        <FiltersBlock>
          <Toolbar>
            <SearchForm onSubmit={handleSearch}>
              <SearchInput
                type="search"
                placeholder={t(`${p}.searchPlaceholder`)}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label={t(`${p}.searchAria`)}
              />
              <SearchBtn type="submit">{t(`${p}.searchBtn`)}</SearchBtn>
            </SearchForm>

            {!tagsLoading && tags.length > 0 ? (
              <FilterGroup>
                <FilterLabel htmlFor="journal-tag-filter">{t(`${p}.filterByTag`)}</FilterLabel>
                <Select
                  id="journal-tag-filter"
                  value={activeTag}
                  onChange={handleTagChange}
                  aria-label={t(`${p}.filterByTag`)}
                >
                  <option value="">{t(`${p}.filterAllTags`)}</option>
                  {tags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </Select>
              </FilterGroup>
            ) : null}

            <FilterGroup>
              <FilterLabel htmlFor="journal-sort">{t(`${p}.sortLabel`)}</FilterLabel>
              <Select id="journal-sort" value={sort} onChange={handleSortChange} aria-label={t(`${p}.sortLabel`)}>
                <option value="newest">{t(`${p}.sortNewest`)}</option>
                <option value="oldest">{t(`${p}.sortOldest`)}</option>
              </Select>
            </FilterGroup>
          </Toolbar>

          {hasActiveFilters ? (
            <ActiveFilters>
              {searchQ.trim() ? (
                <FilterChip>
                  “{searchQ.trim()}”
                  <ChipBtn
                    type="button"
                    aria-label={t(`${p}.clearFilters`)}
                    onClick={() => pushQuery({ q: '', page: 1 }, { scrollTop: true })}
                  >
                    <X size={14} />
                  </ChipBtn>
                </FilterChip>
              ) : null}
              {activeTag ? (
                <FilterChip>
                  {activeTag}
                  <ChipBtn
                    type="button"
                    aria-label={t(`${p}.clearFilters`)}
                    onClick={() => pushQuery({ tag: '', page: 1 }, { scrollTop: true })}
                  >
                    <X size={14} />
                  </ChipBtn>
                </FilterChip>
              ) : null}
              {sort === 'oldest' ? <FilterChip>{t(`${p}.sortOldest`)}</FilterChip> : null}
              <ClearLink type="button" onClick={clearFilters}>
                {t(`${p}.clearFilters`)}
              </ClearLink>
            </ActiveFilters>
          ) : null}
        </FiltersBlock>

        {error ? (
          <Empty>
            <p>{error}</p>
            <Button type="button" $variant="outlineLuxe" onClick={load} disabled={loading} style={{ marginTop: 16 }}>
              {t(`${p}.retry`)}
            </Button>
          </Empty>
        ) : loading && !posts.length ? (
          <Empty>{t(`${p}.loading`)}</Empty>
        ) : !posts.length ? (
          <Empty>{t(`${p}.emptyPosts`)}</Empty>
        ) : (
          <>
            <GridWrap $busy={loading && posts.length > 0}>
              <Grid>
                {posts.map((post) => {
                  const slug = post.slug || post._id;
                  if (!slug) return null;
                  return (
                    <Card key={post._id || slug} to={`/blog/${slug}`}>
                      <Thumb>
                        {post.featuredImage ? (
                          <ThumbImg src={post.featuredImage} alt="" loading="lazy" />
                        ) : (
                          <ThumbPlaceholder aria-hidden />
                        )}
                      </Thumb>
                      <Meta>
                        {formatDate(post.publishedAt)}
                        {Array.isArray(post.tags) && post.tags[0] ? ` · ${post.tags[0]}` : ''}
                      </Meta>
                      <CardTitle>{post.title || t(`${p}.untitledPost`)}</CardTitle>
                      {post.excerpt ? <Excerpt>{post.excerpt}</Excerpt> : null}
                    </Card>
                  );
                })}
              </Grid>
            </GridWrap>

            <JournalPagination
              page={currentPage}
              pages={totalPages}
              total={total}
              limit={limit}
              disabled={loading}
              onPageChange={(next) => pushQuery({ page: next }, { scrollTop: true })}
              previousLabel={t(`${p}.previous`)}
              nextLabel={t(`${p}.next`)}
              summaryLabel={({ from, to, total: tot, current, totalPages: tp }) =>
                tot
                  ? `${t(`${p}.showingArticles`, { from, to, total: tot })} · ${t(`${p}.pageOf`, { current, total: tp })}`
                  : t(`${p}.pageOf`, { current, total: tp })
              }
            />
          </>
        )}
      </PageShell>
    </Container>
  );
}
